/**
 * useQuery — Hook de data fetching com cache, loading e erro
 * Substitui o padrão useEffect + useState manual por toda a app.
 * Sem dependências externas — funciona com Vite + React 18.
 *
 * Uso:
 *   const { data, loading, error, refetch } = useQuery(
 *     () => fetchLatestSnapshot(clientId),
 *     [clientId],           // deps — refetch quando mudar
 *     { ttl: 60000 }        // cache de 60s (opcional)
 *   );
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { logError } from "./shared/logger.js";

// Cache global em memória — persiste enquanto o app está aberto
// LRU eviction: remove entrada mais antiga se exceder MAX_CACHE
const MAX_CACHE = 50;
const _cache = new Map();

function getCacheKey(key, deps) {
  try {
    return key + "|" + JSON.stringify(deps);
  } catch {
    return key + "|" + String(Math.random());
  }
}

/**
 * @param {Function} fetcher  Função async que retorna os dados
 * @param {Array}    deps     Dependências (re-fetch quando mudam)
 * @param {Object}   options
 *   @param {string}    options.key        Chave única para cache (recomendado para produção)
 *   @param {number}   options.ttl        Tempo de vida do cache em ms (default: 30s)
 *   @param {boolean}  options.skip       Se true, não faz fetch
 *   @param {*}        options.fallback   Valor inicial enquanto carrega
 */
export function useQuery(
  fetcher,
  deps = [],
  { key = "", ttl = 30_000, skip = false, fallback = null } = {}
) {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async (force = false) => {
    if (skip) return;

    const cacheKey = getCacheKey(key || "q", deps);
    const cached = _cache.get(cacheKey);

    // Serve do cache se ainda válido e não é refetch forçado
    if (!force && cached && Date.now() - cached.ts < ttl) {
      if (mountedRef.current) {
        setData(cached.data);
        setLoading(false);
      }
      return cached.data;
    }

    if (mountedRef.current) setLoading(true);
    try {
      const result = await fetcher();
      if (_cache.size >= MAX_CACHE) {
        const oldest = _cache.keys().next().value;
        _cache.delete(oldest);
      }
      _cache.set(cacheKey, { data: result, ts: Date.now() });
      if (mountedRef.current) {
        setData(result);
        setError(null);
      }
      return result;
    } catch (e) {
      if (mountedRef.current) setError(e?.message || String(e));
      logError("useQuery", e, { context: "fetch", key });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  const refetch = useCallback(() => run(true), [run]);
  const invalidate = useCallback(() => {
    const cacheKey = getCacheKey(key || "q", deps);
    _cache.delete(cacheKey);
  }, deps);

  return { data, loading, error, refetch, invalidate };
}

/**
 * useMutation — Para operações de escrita (INSERT, UPDATE, DELETE)
 * Não faz fetch automático; retorna uma função `mutate` para chamar manualmente.
 *
 * Uso:
 *   const { mutate, loading, error } = useMutation(
 *     (payload) => upsertSnapshot(clientId, payload),
 *     { onSuccess: () => refetch() }
 *   );
 */
export function useMutation(fn, { onSuccess, onError } = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const mutate = useCallback(
    async (...args) => {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await fn(...args);
        if (mountedRef.current) setLoading(false);
        onSuccess?.(result);
        return result;
      } catch (e) {
        if (mountedRef.current) {
          setLoading(false);
          setError(e?.message || String(e));
        }
        onError?.(e);
        throw e;
      }
    },
    [fn, onSuccess, onError]
  );

  return { mutate, loading, error };
}

/**
 * invalidateCache — Limpa todo o cache ou por prefixo
 */
export function invalidateCache(prefix = null) {
  if (!prefix) {
    _cache.clear();
    return;
  }
  for (const key of _cache.keys()) {
    if (key.includes(prefix)) _cache.delete(key);
  }
}

/**
 * Exemplo de uso em ClientLayout (para referência):
 *
 * import { useQuery } from "./useQuery";
 *
 * const { data: snap, loading, refetch } = useQuery(
 *   () => fetchLatestSnapshot(clientId),
 *   [clientId],
 *   { key: "snapshot", ttl: 60_000, fallback: null }
 * );
 */
